/**
 * Shared subscription-summary read (task 964).
 *
 * `GET /api/billing/subscription` is the owner-gated innovate-service proxy for
 * the AMS subscription-summary read (task 627), which task 942 gave two plan
 * faces (the Stripe-billed plan alongside the AMS entitlement plan) and task 963
 * gave the Stripe period start. Three billing surfaces now render it —
 * SubscriptionPage, SettingsBillingPage and the legacy BillingPage — so this
 * module is the single definition of that query rather than a third copy of the
 * same `['subscriptionSummary']` key.
 *
 * Failure posture (unchanged from the copies it replaces): the endpoint may be
 * absent (not deployed), forbidden (not an owner) or failing. None of those are
 * error states for a billing page — the Stripe-derived lines simply do not
 * render. The query therefore resolves to `null` rather than throwing, and does
 * not retry.
 */

import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { SubscriptionSummary } from '../types/billing';

/** The query key every subscription-summary reader shares. */
export const SUBSCRIPTION_SUMMARY_QUERY_KEY = ['subscriptionSummary'] as const;

/**
 * The account's Stripe subscription summary, or `null` when the read is
 * unavailable. `enabled` lets a page skip the call entirely for an account with
 * no plan (which has no Stripe customer to summarise).
 */
export const useSubscriptionSummary = (enabled: boolean = true) =>
  useQuery<SubscriptionSummary | null>({
    queryKey: SUBSCRIPTION_SUMMARY_QUERY_KEY,
    enabled,
    queryFn: async () => {
      try {
        const res = await api.get<{ success: boolean; data: SubscriptionSummary }>(
          '/billing/subscription',
        );
        return res.data.data;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
